import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { RotateCcw, AlertTriangle, CheckCircle, Search, X } from 'lucide-react';
import api from '../../../services/api';
import studyService from '../../../services/studyService';
import siteService from '../../../services/siteService';
import participantService from '../../../services/participantService';

const IDENTIFIER_MODES = [
  { value: 'email', label: 'Email' },
  { value: 'participant_id', label: 'Participant ID' },
  { value: 'participant_uuid', label: 'UUID' },
];

const ParticipantResetTool = () => {
  const [idMode, setIdMode] = useState('participant_id');
  const [identifier, setIdentifier] = useState('');
  const [survey, setSurvey] = useState('');
  const [keepHistory, setKeepHistory] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  // Cascading picker state
  const [studies, setStudies] = useState([]);
  const [sites, setSites] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [studyId, setStudyId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [participantFilter, setParticipantFilter] = useState('');
  const [loadingStudies, setLoadingStudies] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingParticipants, setLoadingParticipants] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingStudies(true);
      try {
        const response = await studyService.getStudies();
        if (cancelled) return;
        const list = Array.isArray(response) ? response : [];
        setStudies(list.filter(s => s && s.id));
      } catch (err) {
        if (!cancelled) toast.error('Could not load studies');
      } finally {
        if (!cancelled) setLoadingStudies(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setSites([]);
    setSiteId('');
    setParticipants([]);
    setSelectedParticipant(null);
    if (!studyId) return;
    let cancelled = false;
    const load = async () => {
      setLoadingSites(true);
      try {
        const list = await siteService.getSitesForStudy(studyId);
        if (cancelled) return;
        setSites(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled) toast.error('Could not load sites');
      } finally {
        if (!cancelled) setLoadingSites(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [studyId]);

  useEffect(() => {
    setParticipants([]);
    setSelectedParticipant(null);
    setParticipantFilter('');
    if (!studyId || !siteId) return;
    let cancelled = false;
    const load = async () => {
      setLoadingParticipants(true);
      try {
        const list = await participantService.getParticipantsForSite(studyId, siteId);
        if (cancelled) return;
        setParticipants(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled) toast.error('Could not load participants');
      } finally {
        if (!cancelled) setLoadingParticipants(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [studyId, siteId]);

  const choosePickerParticipant = (participant) => {
    setSelectedParticipant(participant);
    if (participant?.participant_id) {
      setIdMode('participant_id');
      setIdentifier(participant.participant_id);
    } else if (participant?.id) {
      setIdMode('participant_id');
      setIdentifier(String(participant.id));
    }
  };

  const clearPicker = () => {
    setStudyId('');
    setSiteId('');
    setSelectedParticipant(null);
    setParticipantFilter('');
  };

  const filteredParticipants = participants.filter(p => {
    if (!participantFilter.trim()) return true;
    const needle = participantFilter.trim().toLowerCase();
    return [
      p.participant_id,
      p.participant_number,
      p.email,
      String(p.id ?? ''),
    ]
      .filter(Boolean)
      .some(v => String(v).toLowerCase().includes(needle));
  });

  const runReset = async () => {
    if (!identifier.trim()) {
      toast.error(`Enter a ${IDENTIFIER_MODES.find(m => m.value === idMode).label}`);
      return;
    }

    setLoading(true);
    setSummary(null);

    try {
      const body = {
        [idMode]: identifier.trim(),
        survey: survey.trim() || undefined,
        keep_history: keepHistory,
        dry_run: dryRun,
      };
      const { data } = await api.post('/api/dev/reset-participant-assignments/', body);
      setSummary(data);
      toast.success(
        data.dry_run
          ? 'Dry run complete — nothing was changed'
          : 'Reset complete'
      );
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Reset failed: ${detail}`);
      setSummary({ error: detail });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
          <RotateCcw className="w-5 h-5 text-primary-600" />
          Reset Test Participant
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Reopens completed survey windows, resets assignments, and (by default) deletes
          response history so a test participant can retake assessments immediately.
          Blocked in production.
        </p>
      </div>

      <div className="border border-gray-200 dark:border-gray-700 rounded p-4 space-y-3 bg-gray-50/50 dark:bg-gray-900/30">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
            <Search className="w-4 h-4 text-primary-600" />
            Find participant
          </h4>
          {(studyId || siteId || selectedParticipant) && (
            <button
              type="button"
              onClick={clearPicker}
              className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 inline-flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Study {loadingStudies && <span className="text-gray-400">(loading…)</span>}
            </label>
            <select
              value={studyId}
              onChange={(e) => setStudyId(e.target.value)}
              disabled={loadingStudies}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
            >
              <option value="">— Select study —</option>
              {studies.map(s => (
                <option key={s.id} value={s.id}>
                  {s.title || s.study_name || `Study ${s.id}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Site {loadingSites && <span className="text-gray-400">(loading…)</span>}
            </label>
            <select
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              disabled={!studyId || loadingSites}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm disabled:opacity-50"
            >
              <option value="">{studyId ? '— Select site —' : 'Pick study first'}</option>
              {sites.map(s => (
                <option key={s.id} value={s.id}>
                  {s.site_name || s.name || `Site ${s.id}`}
                  {s.site_code ? ` (${s.site_code})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {studyId && siteId && (
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Participant {loadingParticipants && <span className="text-gray-400">(loading…)</span>}
              {!loadingParticipants && participants.length > 0 && (
                <span className="text-gray-400"> ({filteredParticipants.length} of {participants.length})</span>
              )}
            </label>
            <input
              type="text"
              value={participantFilter}
              onChange={(e) => setParticipantFilter(e.target.value)}
              placeholder="Filter by participant ID, number, or email…"
              className="w-full px-3 py-2 mb-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
            />
            <div className="max-h-56 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {loadingParticipants ? (
                <div className="p-3 text-sm text-gray-500">Loading…</div>
              ) : filteredParticipants.length === 0 ? (
                <div className="p-3 text-sm text-gray-500">
                  {participants.length === 0 ? 'No participants at this site.' : 'No matches.'}
                </div>
              ) : (
                filteredParticipants.map(p => {
                  const isSelected = selectedParticipant?.id === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => choosePickerParticipant(p)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 dark:hover:bg-primary-900/20 ${
                        isSelected ? 'bg-primary-100 dark:bg-primary-900/40' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-mono text-gray-900 dark:text-gray-100 truncate">
                            {p.participant_id || `#${p.id}`}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            #{p.participant_number ?? '—'} · {p.site?.site_name || '—'}
                            {p.last_login && ` · last login ${new Date(p.last_login).toLocaleDateString()}`}
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                          p.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {p.is_active ? 'active' : 'inactive'}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {selectedParticipant && (
          <div className="flex items-center gap-2 text-xs text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded px-3 py-2">
            <CheckCircle className="w-4 h-4" />
            Will reset <span className="font-mono">{selectedParticipant.participant_id || `#${selectedParticipant.id}`}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Identify by
          </label>
          <select
            value={idMode}
            onChange={(e) => setIdMode(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            {IDENTIFIER_MODES.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {IDENTIFIER_MODES.find(m => m.value === idMode).label}
          </label>
          <input
            type="text"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder={idMode === 'email' ? 'jake+test@example.com' : idMode === 'participant_id' ? '9999-1818' : 'uuid'}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Survey filter (optional)
        </label>
        <input
          type="text"
          value={survey}
          onChange={(e) => setSurvey(e.target.value)}
          placeholder="Survey id or name. Leave blank to reset every survey for the participant."
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div className="flex flex-wrap gap-6">
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
          />
          Dry run (preview, no changes)
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={keepHistory}
            onChange={(e) => setKeepHistory(e.target.checked)}
          />
          Keep history (preserve CompletedSurveys & responses)
        </label>
      </div>

      {!dryRun && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800 dark:text-amber-200">
            <strong>Dry run is off.</strong> Running will delete CompletedSurvey and
            ParticipantResponse rows (unless Keep history is checked).
          </div>
        </div>
      )}

      <button
        onClick={runReset}
        disabled={loading}
        className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
      >
        {loading ? 'Running…' : (dryRun ? 'Preview reset' : 'Run reset')}
      </button>

      {summary && !summary.error && (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded space-y-2">
          <div className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
            <CheckCircle className="w-5 h-5 text-green-600" />
            {summary.dry_run ? 'Dry run — no changes made' : 'Reset complete'}
          </div>
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <div>
              <span className="font-mono">{summary.participant.email}</span>{' '}
              <span className="text-gray-500">
                ({summary.participant.participant_id}, {summary.participant.uuid})
              </span>
            </div>
            {summary.survey_filter && (
              <div>Scope: <span className="font-mono">{summary.survey_filter}</span></div>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
            <Counter label="Windows" value={summary.counts.windows} />
            <Counter label="Assignments" value={summary.counts.assignments} />
            <Counter
              label="Completed surveys"
              value={summary.counts.completed_surveys}
              sub={summary.keep_history ? 'preserved' : 'deleted'}
            />
            <Counter
              label="Responses"
              value={summary.counts.responses}
              sub={summary.keep_history ? 'preserved' : 'deleted'}
            />
          </div>
          {!summary.dry_run && (
            <div className="pt-2 text-sm text-gray-600 dark:text-gray-400">
              Windows: {summary.window_statuses.open} open,{' '}
              {summary.window_statuses.pending} pending,{' '}
              {summary.window_statuses.closed} closed (past end_time)
            </div>
          )}
        </div>
      )}

      {summary?.error && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-800 dark:text-red-200">
          {summary.error}
        </div>
      )}
    </div>
  );
};

const Counter = ({ label, value, sub }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-3">
    <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
    {sub && <div className="text-xs text-gray-500 dark:text-gray-400">{sub}</div>}
  </div>
);

export default ParticipantResetTool;
